/**
 * Tasted motion tokens — the shared animation vocabulary for apps/game and
 * apps/game-lite.
 *
 * Durations + easings only. No `gsap` import, no side effects, so this is safe
 * to pull into an App shell or a lazy route chunk without dragging the GSAP
 * bundle along. The GSAP `fx` builders stay in each app's own `src/lib/anims.js`
 * — game's set is far larger than Lite's, and that difference is deliberate.
 *
 * Why it lives here: both apps' `anims.js` opened with the line "Single source
 * of truth for durations, easings, and effect builders" while being two separate
 * files, and they had quietly drifted apart — most sharply on `EASE.spring`
 * (`elastic.out(1, 0.4)` in game vs `(1, 0.5)` in Lite, applied by the *same*
 * `fx.pop` builder in each). This file is now that single source of truth, and
 * the values below are apps/game's, which is the more developed set.
 *
 * Rule of thumb: need `DUR` / `EASE` / `EASE_BEZIER` in a file that doesn't need
 * `gsap`? Import from here. Otherwise import from the app's `lib/anims`, which
 * re-exports all of this so existing call sites keep working.
 */

// ─── Durations (seconds) ────────────────────────────────────────────────────

export const DUR = {
  micro:   0.07, // sub-frame flash — brightness hits
  fast:    0.15, // quick feedback, exits
  med:     0.30, // standard transitions
  slow:    0.50, // emphasis, entrances
  slower:  0.80, // long settle — celebration / crown beats
  ambient: 1.70, // slow looping ambient pulses (reveal halos)
};

// ─── Stagger intervals (seconds) ────────────────────────────────────────────
// The gap between one item of a list and the next. Each app's `anims.js` has
// carried these two numbers privately for its `fx` builders; they are named
// here so a file that must not import `anims.js` (and GSAP with it) can still
// use the same beat.

export const STAGGER = {
  enter: 0.07, // items arriving
  exit:  0.04, // items leaving
};

// ─── Easings (GSAP format) ──────────────────────────────────────────────────

export const EASE = {
  snap:   'power3.out',          // fast deceleration — entrances
  smooth: 'power2.inOut',        // balanced — state changes
  spring: 'elastic.out(1, 0.4)', // bouncy — celebration
  in:     'power2.in',           // acceleration — exits
  out:    'power2.out',          // gentle deceleration — common GSAP tweens
  back:   'back.out(1.4)',       // weighted overshoot settle — reveals, card flips
};

// ─── Framer-motion easings (cubic-bezier arrays) ────────────────────────────
// The EASE values above are GSAP-format strings; framer-motion `transition.ease`
// needs cubic-bezier arrays. Centralize the recurring ones here so screens
// don't carry bare bezier literals.

export const EASE_BEZIER = {
  softOut: [0.22, 1, 0.36, 1],    // gentle ease-out — curtain, sheet & card transitions
  wine:    [0.15, 0.85, 0.35, 1], // brand ease — wine review / reveal
};

// ─── Motion tiering ─────────────────────────────────────────────────────────
// Two gates, and picking the wrong one is the usual mistake (see CLAUDE.md).
//
//   prefersReducedMotion()  ESSENTIAL transitions. The player asked for less
//                           motion, but the transition still has to HAPPEN —
//                           fade instead of choreograph. A sheet returning to
//                           rest, a screen replacing another, a modal arriving.
//
//   liteMotion()            DECORATIVE motion. Reduced-motion OR a device that
//                           can't afford it — budget hardware, in-app browser
//                           (TAS-311). Skip it entirely. Particle spawners,
//                           icon loops, confetti, springs, anything paint-heavy.
//
// The second gate matters especially for Game Lite: it reaches players through a
// QR code at a venue, so a large share of sessions open inside Instagram or
// WhatsApp webviews on mid-range Androids — exactly the tier `isLitePerf` looks
// for.
//
// Checked once at animation start (repo pattern — see createGiftTear).

import { isLitePerf } from './perfTier.js';

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);

export const liteMotion = () => prefersReducedMotion() || isLitePerf();
